import { Alert } from "@/components/ui/Alert";
import { formatDollarsRound } from "@/customer/catalog/pricing";

import type { QuoteLine, QuoteResponse } from "./types";

// Read-only display of a Pricing::Quote::Result. On `ok=false` we show
// the server's error messages and omit the total — the caller renders
// nothing else to imply the quote is invalid.

interface Props {
  quote: QuoteResponse;
}

export function PriceBreakdown({ quote }: Props): JSX.Element {
  if (!quote.ok) {
    return (
      <Alert variant="destructive">
        <ul className="list-disc space-y-0.5 pl-5">
          {Object.entries(quote.errors).flatMap(([field, msgs]) =>
            msgs.map((msg, idx) => <li key={`${field}-${idx}`}>{msg}</li>)
          )}
        </ul>
      </Alert>
    );
  }

  return (
    <div className="rounded-md border border-zinc-200 bg-brand-white">
      <ul className="divide-y divide-zinc-100">
        {quote.lines.map((line, i) => (
          <LineRow key={`${line.section}-${i}`} line={line} />
        ))}
      </ul>
      <div
        data-testid="price-total"
        className="flex items-baseline justify-between border-t-2 border-zinc-200 px-4 py-3"
      >
        <span className="text-base font-semibold text-brand-black">
          Estimated total ({quote.duration_days} day{quote.duration_days === 1 ? "" : "s"})
        </span>
        <span className="text-lg font-semibold text-brand-black">
          {formatDollarsRound(quote.total_cents)}
        </span>
      </div>
      <p className="border-t border-zinc-100 px-4 py-2 text-xs text-zinc-500">
        Tax and any card processing surcharge are computed at checkout.
      </p>
    </div>
  );
}

function LineRow({ line }: { line: QuoteLine }): JSX.Element {
  return (
    <li className="flex items-baseline justify-between gap-3 px-4 py-2 text-sm">
      <div className="min-w-0">
        <p className="truncate font-medium text-brand-black">{line.label}</p>
        {line.detail ? <p className="truncate text-xs text-zinc-500">{line.detail}</p> : null}
      </div>
      <span className="shrink-0 tabular-nums text-brand-black">
        {formatDollarsRound(line.amount_cents)}
      </span>
    </li>
  );
}
